import React, { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import type { Business } from '../../contexts/BusinessContext';
import { GiftCardSettings } from '../../services/giftCardSettingsService';
import GiftCardWrapper from '../../components/GiftCardWrapper';
import { CheckCircle, Mail, Gift, AlertCircle } from 'lucide-react';

interface GiftCardData {
  id: string;
  code: string;
  initial_amount: number;
  purchaser: {
    first_name: string;
    last_name: string;
    email: string;
    phone?: string;
  };
  recipient_name?: string;
  recipient_email?: string;
}

export default function GiftCardSuccess() {
  const { businessSlug } = useParams<{ businessSlug: string }>();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [business, setBusiness] = useState<Business | null>(null);
  const [giftCard, setGiftCard] = useState<GiftCardData | null>(null);
  const [design, setDesign] = useState<GiftCardSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function loadData() {
      if (!businessSlug) {
        setError('Business not found');
        setLoading(false);
        return;
      }

      if (!supabase) {
        console.error('Supabase client is not initialized');
        setError('Unable to connect to the database');
        setLoading(false);
        return;
      }

      try {
        // Load business data
        const { data: businessData, error: businessError } = await supabase
          .from('businesses')
          .select('*')
          .eq('slug', businessSlug)
          .single();

        if (businessError || !businessData) {
          console.error('Error fetching business:', businessError);
          setError('Business not found');
          setLoading(false);
          return;
        }

        setBusiness(businessData);

        // If we have a session ID, try to find the gift card
        // Note: The webhook may take a moment to process, so we'll retry a few times
        if (sessionId) {
          let attempts = 0;
          const maxAttempts = 10;
          const retryDelay = 1000; // 1 second

          const findGiftCard = async (): Promise<void> => {
            // Find payment by session ID (also get metadata for design_id)
            const { data: paymentData, error: paymentError } = await supabase
              .from('payments')
              .select('gift_card_id, notes')
              .eq('stripe_checkout_session_id', sessionId)
              .single();

            if (!paymentError && paymentData?.gift_card_id) {
              // Load gift card data
              const { data: giftCardData, error: giftCardError } = await supabase
                .from('gift_cards')
                .select(`
                  id,
                  code,
                  initial_amount,
                  purchaser_customer_id,
                  recipient_name,
                  recipient_email,
                  customers:purchaser_customer_id (
                    first_name,
                    last_name,
                    email,
                    phone
                  )
                `)
                .eq('id', paymentData.gift_card_id)
                .eq('business_id', businessData.id)
                .single();

              if (!giftCardError && giftCardData) {
                const customerData = Array.isArray(giftCardData.customers)
                  ? giftCardData.customers[0]
                  : giftCardData.customers;

                setGiftCard({
                  id: giftCardData.id,
                  code: giftCardData.code,
                  initial_amount: giftCardData.initial_amount,
                  purchaser: {
                    first_name: (customerData as any)?.first_name || '',
                    last_name: (customerData as any)?.last_name || '',
                    email: (customerData as any)?.email || '',
                    phone: (customerData as any)?.phone || ''
                  },
                  recipient_name: giftCardData.recipient_name || undefined,
                  recipient_email: giftCardData.recipient_email || undefined
                });

                // Try to get design ID from Stripe session metadata
                // First, try to get it from the Stripe session via edge function or directly query
                // For now, we'll load the first design as fallback
                // TODO: Store design_id in gift_cards table or get from payment metadata
                const { data: designsData } = await supabase
                  .from('gift_card_settings')
                  .select('*')
                  .eq('business_id', businessData.id)
                  .order('created_at', { ascending: false })
                  .limit(1);

                if (designsData && designsData.length > 0) {
                  setDesign(designsData[0]);
                }
              } else if (attempts < maxAttempts) {
                // Gift card not found yet, retry after delay
                attempts++;
                setTimeout(findGiftCard, retryDelay);
              }
            } else if (attempts < maxAttempts) {
              // Payment not found yet, retry after delay
              attempts++;
              setTimeout(findGiftCard, retryDelay);
            }
          };

          // Start looking for the gift card
          findGiftCard();
        }
      } catch (err) {
        console.error('Error loading data:', err);
        setError('Failed to load gift card information');
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [businessSlug, sessionId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (error || !business) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="h-8 w-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Error</h1>
          <p className="text-gray-700">{error || 'Business not found'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Top bar */}
      <div 
        className="h-24 w-full flex items-center justify-start px-6 sm:px-8" 
        style={{ backgroundColor: business?.booking_settings?.navbar_color || '#000000' }}
      >
        {business?.booking_settings?.logo_url && (
          <img 
            src={business.booking_settings.logo_url} 
            alt={`${business.name} logo`}
            className="h-16 max-w-xs object-contain"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        )}
      </div>
      
      <div className="flex-1 flex items-center justify-center p-4 py-12">
        <div className="max-w-2xl w-full bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Success Header */}
          <div className="bg-gradient-to-r from-green-500 to-emerald-600 px-8 py-12 text-center">
            <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-12 w-12 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">Gift Card Purchase Confirmed!</h1>
            <p className="text-green-50 text-lg">
              Thank you for your purchase. Your gift card has been sent via email.
            </p>
          </div>

          {/* Content */}
          <div className="p-8">
            {giftCard && (
              <div className="space-y-6">
                {/* Gift Card Details */}
                <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                    <Gift className="h-5 w-5 mr-2 text-blue-600" />
                    Gift Card Details
                  </h2>
                  
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Gift Card Code</p>
                      <p className="text-2xl font-bold text-green-600 font-mono tracking-wider">
                        {giftCard.code}
                      </p>
                    </div>
                    
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Amount</p>
                      <p className="text-2xl font-bold text-gray-900">
                        ${giftCard.initial_amount.toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Gift Card Preview */}
                {design && (
                  <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Your Gift Card Design</h3>
                    <div className="flex justify-center">
                      <GiftCardWrapper
                        settings={design}
                        businessLogoUrl={business.booking_settings?.logo_url}
                        showSampleData={false}
                        giftCardCode={giftCard.code}
                        maxWidth="400px"
                      />
                    </div>
                  </div>
                )}

                {/* Purchaser Information */}
                <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <Mail className="h-5 w-5 mr-2 text-blue-600" />
                    Purchaser Information
                  </h3>
                  
                  <div className="space-y-2 text-gray-700">
                    <p>
                      <span className="font-medium">Name:</span>{' '}
                      {giftCard.purchaser.first_name} {giftCard.purchaser.last_name}
                    </p>
                    <p>
                      <span className="font-medium">Email:</span>{' '}
                      {giftCard.purchaser.email}
                    </p>
                    {giftCard.purchaser.phone && (
                      <p>
                        <span className="font-medium">Phone:</span>{' '}
                        {giftCard.purchaser.phone}
                      </p>
                    )}
                  </div>
                </div>

                {/* Recipient Information (if applicable) */}
                {giftCard.recipient_name && (
                  <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Recipient Information</h3>
                    
                    <div className="space-y-2 text-gray-700">
                      <p>
                        <span className="font-medium">Name:</span>{' '}
                        {giftCard.recipient_name}
                      </p>
                      {giftCard.recipient_email && (
                        <p>
                          <span className="font-medium">Email:</span>{' '}
                          {giftCard.recipient_email}
                        </p>
                      )}
                    </div>
                    
                    <div className="mt-4 p-3 bg-white rounded border border-blue-200">
                      <p className="text-sm text-gray-600">
                        The gift card has been sent to {giftCard.recipient_email || giftCard.purchaser.email}
                      </p>
                    </div>
                  </div>
                )}

                {/* Email Confirmation */}
                <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
                  <div className="flex items-start">
                    <Mail className="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">Email Confirmation</h3>
                      <p className="text-gray-700 text-sm">
                        A confirmation email with your gift card details has been sent to{' '}
                        <span className="font-medium">
                          {giftCard.recipient_email || giftCard.purchaser.email}
                        </span>
                        . Please check your inbox (and spam folder) for the email containing your gift card code.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Next Steps */}
                <div className="pt-6 border-t border-gray-200">
                  <div className="flex flex-col sm:flex-row gap-4">
                    <Link
                      to={`/${businessSlug}/gift-cards`}
                      className="flex-1 px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors text-center"
                    >
                      Purchase Another Gift Card
                    </Link>
                    {business.booking_settings?.enable_online_booking && (
                      <Link
                        to={`/${businessSlug}/book`}
                        className="flex-1 px-6 py-3 bg-gray-200 text-gray-900 font-medium rounded-lg hover:bg-gray-300 transition-colors text-center"
                      >
                        Book a Service
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            )}

            {!giftCard && (
              <div className="text-center py-8">
                <p className="text-gray-600 mb-4">
                  Your payment was successful! Your gift card is being processed and you will receive an email confirmation shortly.
                </p>
                <Link
                  to={`/${businessSlug}/gift-cards`}
                  className="inline-block px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Return to Gift Cards
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

